// Design constraints schema
// User-defined constraints applied post-calculation to validate that
// the solved system stays within acceptable engineering limits.

import { z } from 'zod';
import { ValueWithUnitSchema } from '../base/common';

/**
 * Design Constraints Schema
 *
 * Every field is optional — a model can opt into just the constraints
 * relevant to its workflow. These are applied by post-calculation
 * analyzers (velocity check, pressure check, etc.) against the solved
 * system state.
 *
 * Extracted into its own file so it can be reused outside
 * `HydraulicSystemSchema` — notably by the custom-templates feature,
 * which serializes a subset of the system config as a reusable preset
 * and needs to Zod-validate the constraint block on load.
 */
export const DesignConstraintsSchema = z.object({
  /** Maximum pipe velocity the analyzer should flag above (e.g. 8 ft/s for chilled water). */
  max_velocity: ValueWithUnitSchema.optional(),
  /** Minimum pipe velocity the analyzer should flag below (e.g. 0.5 ft/s for corrosion protection). Dead legs (near-zero flow or closed) are excluded. */
  min_velocity: ValueWithUnitSchema.optional(),
  /** Minimum static pressure at any flow-demand vertex (e.g. 20 psi at a fixture). */
  min_pressure_at_demands: ValueWithUnitSchema.optional(),
  /** Maximum total pressure drop budget across a flow path (e.g. 10 psi). */
  max_pressure_drop_budget: ValueWithUnitSchema.optional(),
  /** Maximum allowable pressure at any pipe endpoint (e.g. 150 psig for system rated pressure). */
  max_pressure: ValueWithUnitSchema.optional(),
});

export type DesignConstraints = z.infer<typeof DesignConstraintsSchema>;
